import { useCallback, useState } from "react";
import ItemContactForm from "./ItemPage.ItemContactForm";
import { message } from "antd";
import { useRecaptcha } from "@/lib/recaptcha.client";
import { RECAPTCHA_ACTIONS } from "@/lib/form-guard";

export default function ItemActions({ item }: { item: any }) {
    const [messageApi, contextHolder] = message.useMessage();
    const [isVerified, setIsVerified] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Shared loader: reference-counted, so the script stays alive for the
    // inquiry form this component swaps in after verification. The previous
    // hand-rolled loader tore the script down on its own cleanup, which
    // raced the form that still needs grecaptcha to mint its submit token.
    const { ready: scriptLoaded, disabled: recaptchaDisabled, execute } = useRecaptcha();

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({ url: window.location.href });
            } catch (error) {
                console.error('Error sharing:', error);
            }
        } else {
            alert('Sorry, sharing is not supported on this device/browser.');
        }
    };

    const handleVerification = useCallback(async () => {
        if (recaptchaDisabled) {
            setError("reCAPTCHA is not configured");
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const token = await execute(RECAPTCHA_ACTIONS.viewContactInfo);
            if (!token) throw new Error("Verification failed");

            // Send token to server for verification
            const response = await fetch("/api/verify-recaptcha", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ token }),
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || "Verification failed");
            }

            setIsVerified(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Verification failed");
        } finally {
            setIsLoading(false);
        }
    }, [recaptchaDisabled, execute]);

    return (
        <>
            {contextHolder}

            {!isVerified ? (
                <div className="flex flex-col lg:flex-row gap-4 w-full">
                    <button
                        onClick={handleVerification}
                        disabled={isLoading || !scriptLoaded}
                        className="inline-flex items-center justify-center gap-2 hover:cursor-pointer w-full lg:w-1/2 bg-gdeepcopper hover:bg-gdeepercopper active:bg-gdeepercopper disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
                    >
                        {isLoading
                            ? "Verifying..."
                            : scriptLoaded
                                ? (
                                    <>
                                        <svg
                                            className="w-5 h-5"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                            />
                                        </svg>
                                        Inquire About This Item
                                    </>
                                )
                                : "Loading..."}
                    </button>
                    <div onClick={handleShare} className="w-full lg:w-1/2 inline-flex hover:cursor-pointer items-center justify-center gap-2 bg-white border border-gdeepcopper text-gdeepcopper hover:text-gdeepercopper hover:border-gdeepercopper active:text-gdeepercopper active:border-gdeepercopper font-semibold py-3 px-6 rounded-lg transition-colors duration-200">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z" />
                        </svg>
                        Share this item
                    </div>
                </div>
            ) : (
                <ItemContactForm item={item} setFormVisible={setIsVerified} messageApi={messageApi} />
            )}

            {error && <p className="text-red-500 text-sm mt-2">{error}</p>}

        </>
    );
}